import { useAiPolicy, type AiPolicy } from "@/hooks/useAiPolicy";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ShieldCheck } from "lucide-react";
import { useState } from "react";

const OPTIONS: { value: AiPolicy; label: string; hint: string }[] = [
  {
    value: "no-ai",
    label: "No generative AI allowed",
    hint: "My company blocks AI tools entirely",
  },
  {
    value: "copilot-only",
    label: "Microsoft Copilot only",
    hint: "AI is allowed, but only through Copilot",
  },
  {
    value: "most-ai-allowed",
    label: "Most AI tools are allowed",
    hint: "Show everything, including AI features",
  },
];

/**
 * A quiet, opt-in preference -- not a first-visit interruption. No AI
 * features exist on the site yet, so nothing is actually hidden or gated
 * by this today; setting a preference now just means the first AI feature
 * that ships will already respect it, rather than asking again later.
 */
export function AiPolicySelector({ trigger }: { trigger: React.ReactNode }) {
  const { policy, setPolicy } = useAiPolicy();
  const [selected, setSelected] = useState<AiPolicy>(
    policy === "unset" ? "most-ai-allowed" : policy
  );
  const [open, setOpen] = useState(false);

  const save = () => {
    setPolicy(selected);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <div onClick={() => setOpen(true)}>{trigger}</div>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            Company AI Policy
          </DialogTitle>
          <DialogDescription>
            This only changes what appears on FreeDailyPro for you — nothing is
            shared with your employer, and none of our browser-based tools
            ever require this to work.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          {OPTIONS.map(opt => (
            <label
              key={opt.value}
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                selected === opt.value
                  ? "border-primary bg-primary/5"
                  : "border-border"
              }`}
            >
              <input
                type="radio"
                name="ai-policy"
                className="mt-1"
                checked={selected === opt.value}
                onChange={() => setSelected(opt.value)}
              />
              <div>
                <p className="text-sm font-medium">{opt.label}</p>
                <p className="text-xs text-muted-foreground">{opt.hint}</p>
              </div>
            </label>
          ))}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={save}>Save preference</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
